import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ForumService from '../../../sdk/services/ForumService';
import { readApiError, type ApiError } from '../../../sdk/errors/apiError';
import type { CommentSort, ForumComment, ForumThread } from '../../../sdk/@types/Forum';

/** Quantos comentários de topo o embed mostra antes do "Ver mais N". */
export const DEFAULT_PAGE_SIZE = 3;

export interface UseForumThreadOptions {
  questionId?: string;
  /**
   * A busca só acontece depois que a questão foi respondida (ou o gabarito
   * revelado) — decisão 4 do design. Sem isso o carrossel somaria uma chamada
   * de fórum a cada navegação de questão, que é a mesma preocupação que fez
   * `useDueCount` usar `getStats` em vez de `getDue`.
   */
  answered: boolean;
  /** Primeira página do embed. */
  pageSize?: number;
  /**
   * O autor é `administrador`/`master`: o comentário dele nasce público, sem
   * fila de moderação (premissa 2 do proposal). Só afeta o palpite otimista —
   * o `status` que a API devolver sempre vence.
   */
  staff?: boolean;
}

export interface UseForumThreadResult {
  sort: CommentSort;
  setSort: (sort: CommentSort) => void;
  /** Comentários de topo já ordenados, filtrados e cortados na página atual. */
  comments: ForumComment[];
  /** Aprovados da questão (topo + respostas) — é o número do cabeçalho. */
  approvedCount: number;
  /** Comentários de topo ainda escondidos atrás do "Ver mais N". */
  remaining: number;
  expanded: boolean;
  expand: () => void;
  loading: boolean;
  /** `null` quando deu tudo certo. `kind` preenchido = negativa de permissão. */
  error: ApiError | null;
  /** Aprovado com mais votos da questão (>= 1 voto) — badge "Mais útil". */
  mostUsefulId: string | null;
  reload: () => void;
  submit: (body: string, parentId?: string) => Promise<void>;
  edit: (id: string, body: string) => Promise<void>;
  vote: (id: string) => Promise<void>;
  flag: (id: string) => Promise<void>;
}

/* -------------------------------------------------------------------------- */
/* Normalização                                                                */
/* -------------------------------------------------------------------------- */

/**
 * O backend do fórum ainda não existe (ver `design.md`), então a resposta é
 * tratada com desconfiança: aceita `{comments,total}` ou um array cru, e
 * completa os campos que o contrato promete mas um stub pode não mandar.
 */
function normalizeComment(raw: ForumComment, depth = 0): ForumComment {
  return {
    ...raw,
    votes: Number.isFinite(raw?.votes) ? Number(raw.votes) : 0,
    voted: Boolean(raw?.voted),
    mine: Boolean(raw?.mine),
    status: raw?.status ?? 'approved',
    replies:
      depth === 0 && Array.isArray(raw?.replies)
        ? raw.replies.map((reply) => normalizeComment(reply, 1))
        : undefined,
  };
}

function normalizeThread(payload: ForumThread | ForumComment[] | null | undefined) {
  const list = Array.isArray(payload) ? payload : payload?.comments;
  return Array.isArray(list) ? list.map((item) => normalizeComment(item)) : [];
}

/* -------------------------------------------------------------------------- */
/* Derivações                                                                  */
/* -------------------------------------------------------------------------- */

const time = (comment: ForumComment) => {
  const value = Date.parse(comment?.createdAt ?? '');
  return Number.isNaN(value) ? 0 : value;
};

const isStaff = (comment: ForumComment) =>
  comment?.author?.role === 'professor' || comment?.author?.role === 'moderador';

/** Só o autor enxerga o próprio comentário fora do estado `approved`. */
const isPublic = (comment: ForumComment) => comment?.status === 'approved';

/** Percorre topo + respostas — as duas listas seguem as mesmas regras. */
function flatten(comments: ForumComment[]): ForumComment[] {
  return comments.flatMap((comment) => [comment, ...(comment.replies ?? [])]);
}

/**
 * A ordenação é derivada da lista já carregada, não de uma nova requisição:
 * trocar de aba tem que reordenar "sem recarregar a página", e o contrato
 * devolve a thread inteira de uma vez (não há `limit`/`offset` em
 * `GET /questions/:id/comments`).
 *
 * Os comentários do próprio usuário que ainda não são públicos ficam presos no
 * topo em "Mais úteis" e "Recentes" — é onde o autor precisa vê-los, e eles não
 * têm votos nem data que os colocasse lá sozinhos. Em "Professores" eles saem,
 * porque aquela aba é um filtro por autor.
 */
export function orderComments(comments: ForumComment[], sort: CommentSort): ForumComment[] {
  if (sort === 'staff') {
    return comments
      .filter((comment) => isStaff(comment) && isPublic(comment))
      .sort((a, b) => b.votes - a.votes || time(b) - time(a));
  }

  const mineFirst = comments.filter((comment) => comment.mine && !isPublic(comment));
  const rest = comments.filter((comment) => !(comment.mine && !isPublic(comment)));

  const byRecent = (a: ForumComment, b: ForumComment) => time(b) - time(a);
  const byUseful = (a: ForumComment, b: ForumComment) => b.votes - a.votes || time(b) - time(a);

  return [
    ...mineFirst.sort(byRecent),
    ...rest.sort(sort === 'recent' ? byRecent : byUseful),
  ];
}

/**
 * "Mais útil" é computado, não escolhido a mão (premissa 5): o aprovado com
 * mais votos da questão, com pelo menos 1 voto, e **um só** mesmo em empate —
 * daí o desempate estável pelo mais antigo.
 */
export function findMostUsefulId(comments: ForumComment[]): string | null {
  const candidates = flatten(comments).filter(
    (comment) => isPublic(comment) && comment.votes >= 1,
  );
  if (!candidates.length) return null;

  return candidates.reduce((best, comment) =>
    comment.votes > best.votes || (comment.votes === best.votes && time(comment) < time(best))
      ? comment
      : best,
  ).id;
}

export function countApproved(comments: ForumComment[]): number {
  return flatten(comments).filter(isPublic).length;
}

/* -------------------------------------------------------------------------- */
/* Hook                                                                        */
/* -------------------------------------------------------------------------- */

/** Aplica `patch` no comentário `id`, esteja ele no topo ou numa resposta. */
function mapComment(
  comments: ForumComment[],
  id: string,
  patch: (comment: ForumComment) => ForumComment,
): ForumComment[] {
  return comments.map((comment) => {
    if (comment.id === id) return patch(comment);
    if (!comment.replies?.length) return comment;
    const replies = comment.replies.map((reply) => (reply.id === id ? patch(reply) : reply));
    return { ...comment, replies };
  });
}

function findComment(comments: ForumComment[], id: string): ForumComment | undefined {
  return flatten(comments).find((comment) => comment.id === id);
}

export default function useForumThread({
  questionId,
  answered,
  pageSize = DEFAULT_PAGE_SIZE,
  staff = false,
}: UseForumThreadOptions): UseForumThreadResult {
  const [comments, setComments] = useState<ForumComment[]>([]);
  const [sort, setSort] = useState<CommentSort>('useful');
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  // Toda mutação precisa ler a lista corrente sem virar dependência de
  // `useCallback` — senão cada voto recria todos os handlers da thread.
  const commentsRef = useRef(comments);
  commentsRef.current = comments;

  // Chave do card enquanto a API não devolve um id — dois comentários seguidos
  // do mesmo tamanho colidiriam se a chave viesse do texto.
  const localIdRef = useRef(0);

  const shouldFetch = Boolean(answered && questionId);

  useEffect(() => {
    if (!shouldFetch || !questionId) {
      setComments([]);
      setError(null);
      setLoading(false);
      return;
    }

    let alive = true;
    setLoading(true);
    setError(null);
    setExpanded(false);

    ForumService.listComments(questionId, 'useful')
      .then((payload) => {
        if (alive) setComments(normalizeThread(payload));
      })
      .catch((err) => {
        // 404 é o caso esperado enquanto as rotas do fórum não existirem no Go.
        // Vira painel de erro dentro da seção; o carrossel continua de pé.
        if (alive) {
          setComments([]);
          setError(readApiError(err));
        }
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [shouldFetch, questionId, reloadKey]);

  // Trocar de questão recomeça a thread do zero, inclusive a aba.
  useEffect(() => {
    setSort('useful');
    setExpanded(false);
  }, [questionId]);

  const ordered = useMemo(() => orderComments(comments, sort), [comments, sort]);
  const visible = expanded ? ordered : ordered.slice(0, pageSize);
  const mostUsefulId = useMemo(() => findMostUsefulId(comments), [comments]);
  const approvedCount = useMemo(() => countApproved(comments), [comments]);

  const reload = useCallback(() => setReloadKey((key) => key + 1), []);

  const submit = useCallback(
    async (body: string, parentId?: string) => {
      const text = body.trim();
      if (!questionId || !text) return;

      const created = await ForumService.createComment(questionId, {
        body: text,
        ...(parentId ? { parentId } : {}),
      });

      localIdRef.current += 1;
      const optimistic: ForumComment = {
        id: `local-${localIdRef.current}`,
        questionId,
        body: text,
        // Admin/master publica direto; aluno entra na fila.
        status: staff ? 'approved' : 'pending',
        author: { id: 'me', name: 'Você', role: staff ? 'professor' : 'aluno' },
        votes: 0,
        voted: false,
        mine: true,
        createdAt: new Date().toISOString(),
        ...(parentId ? { parentId } : {}),
      };

      // O que a API devolveu vence o palpite — exceto `mine`, que é
      // conhecimento local: o POST acabou de sair daqui, e um stub que não
      // devolva o campo faria o card perder o "Editar" e o selo do autor.
      const local: ForumComment = {
        ...optimistic,
        ...(created ?? {}),
        body: created?.body ?? text,
        mine: true,
      };

      const reply = normalizeComment(local, 1);

      setComments((current) => {
        if (!parentId) return [normalizeComment(local), ...current];
        return mapComment(current, parentId, (parent) => ({
          ...parent,
          replies: [...(parent.replies ?? []), reply],
          // A contagem no pai é a das respostas **públicas**: uma resposta de
          // aluno fica em moderação e ninguém mais a vê, então subir o número
          // ali anunciaria uma resposta que não existe para o resto. Quando o
          // pai não trouxe `replyCount`, o card deriva da lista sozinho.
          replyCount:
            parent.replyCount === undefined
              ? undefined
              : parent.replyCount + (reply.status === 'approved' ? 1 : 0),
        }));
      });
    },
    [questionId, staff],
  );

  const edit = useCallback(
    async (id: string, body: string) => {
      const text = body.trim();
      if (!text) return;

      const updated = await ForumService.updateComment(id, text);

      setComments((current) =>
        mapComment(current, id, (comment) => ({
          ...comment,
          ...(updated ?? {}),
          body: updated?.body ?? text,
          // Edição de aluno volta para a fila e some do público; de staff, não.
          status: updated?.status ?? (staff ? 'approved' : 'pending'),
          mine: true,
          replies: comment.replies,
        })),
      );
    },
    [staff],
  );

  const vote = useCallback(async (id: string) => {
    const before = findComment(commentsRef.current, id);
    if (!before || before.mine || before.status !== 'approved') return;

    const voted = !before.voted;
    const votes = Math.max(0, before.votes + (voted ? 1 : -1));

    // Otimista: a contagem mexe no clique (decisão 5).
    setComments((current) => mapComment(current, id, (comment) => ({ ...comment, voted, votes })));

    try {
      const result = await ForumService.voteComment(id);
      if (result && typeof result.votes === 'number') {
        setComments((current) =>
          mapComment(current, id, (comment) => ({
            ...comment,
            votes: result.votes,
            voted: Boolean(result.voted),
          })),
        );
      }
    } catch {
      // Rollback para o estado exato de antes do clique. 401/403 já viraram
      // toast no interceptor — não há tratamento local a fazer.
      setComments((current) =>
        mapComment(current, id, (comment) => ({
          ...comment,
          voted: before.voted,
          votes: before.votes,
        })),
      );
    }
  }, []);

  const flag = useCallback(async (id: string) => {
    const before = findComment(commentsRef.current, id);
    if (!before || before.mine || before.flaggedByMe) return;

    setComments((current) =>
      mapComment(current, id, (comment) => ({ ...comment, flaggedByMe: true })),
    );

    try {
      await ForumService.flagComment(id);
    } catch (err) {
      // 409 = "você já denunciou este comentário": o estado otimista já está
      // certo, desfazê-lo reabriria uma ação que não vai a lugar nenhum.
      if (readApiError(err).status === 409) return;
      setComments((current) =>
        mapComment(current, id, (comment) => ({ ...comment, flaggedByMe: before.flaggedByMe })),
      );
    }
  }, []);

  return {
    sort,
    setSort,
    comments: visible,
    approvedCount,
    remaining: Math.max(0, ordered.length - visible.length),
    expanded,
    expand: () => setExpanded(true),
    loading,
    error,
    mostUsefulId,
    reload,
    submit,
    edit,
    vote,
    flag,
  };
}
